import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, Building, ChevronDown, Search } from "lucide-react";
import { useAllVacancies } from "@/hooks/queries/usevacancy";
import Loader from "@/components/Loader";
import { useGetCampaignsList } from "@/hooks/queries/campaigns";
import { vacancyService } from "@/services/vacancyService";
import useToastStore from "@/components/Common/Toast/toastStore";
import "react-toastify/dist/ReactToastify.css";
import VacancyTable from "@/components/campaign/VacancyTable";
import { useGetCompaniesList } from "@/hooks/queries/useCompanies";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "@/components/Common/Pagination";
import { mapVacancyData } from "@/utils/helpers";
import { useDebounce } from "@/utils/helpers";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import { PER_PAGE } from "@/config";
import usePaginationStore from "@/hooks/stores/usePaginationStore";

const AllVacancies = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const [selectedCompany, setSelectedCompany] = useState();
  const [filteredVacancies, setFilteredVacancies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const { showToast } = useToastStore;
  const { getPerPage, setPerPage } = usePaginationStore();
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState();
  const { activeTab, setActiveTab } = useGlobalStore();

  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage("all-vacancies-table"),
    total: 0,
    last_page: 1,
  });

  // Get all companies
  const { data } = useGetCompaniesList() || {};
  const companies = data?.companies ?? [];
  const message = data?.message ?? "";

  // Get all vacancies by default
  const {
    data: { vacancies: allVacanciesData, pagination } = {
      vacancies: [],
      pagination: {},
    },
    isLoading: isLoadingAllVacancies,
    isError: isErrorAllVacancies,
    refetch: refetchAllVacancies,
  } = useAllVacancies({
    page: serverPagination.current_page,
    per_page: serverPagination.per_page,
    search: searchTerm,
    status: [0, 1],
    company_id: selectedCompany,
    campaign_id: selectedCampaign,
  });

  const {
    data: { campaigns: campaignsData } = { campaigns: [] },
    isLoading: isLoadingCampaigns,
  } = useGetCampaignsList(selectedCompany, {
    // status: [0, 1]
  });
  const campaigns = campaignsData || [];

  // Function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // Debounced search function
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  // Handle search input change
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  // Add this useEffect hook near other hooks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isLoading = isLoadingAllVacancies || isLoadingCampaigns;
  const isError = isErrorAllVacancies && !selectedCompany;

  const handleEdit = (id) => {
    navigate(`/vacancy/${id}`, { replace: true });
    window.location.reload();
  };

  const handleDelete = (vacancyId) => {
    setFilteredVacancies((prevVacancies) =>
      prevVacancies.filter((vacancy) => vacancy.id !== vacancyId),
    );
  };

  const handleStatusChange = async (vacancyId, newStatus) => {
    try {
      await vacancyService.updateVacancyStatus(vacancyId, {
        status: newStatus,
      });

      setFilteredVacancies((prevVacancies) =>
        prevVacancies
          .map((vacancy) =>
            vacancy.id === vacancyId
              ? { ...vacancy, status: newStatus }
              : vacancy,
          )
          ?.filter((item) => item.status != 2),
      );

      showToast({
        message: `Vacancy ${newStatus == 2 ? "archived" : newStatus == 0 ? "marked as Draft" : "published"} successfully`,
        type: "success",
      });

      setActiveMenu(null);
    } catch (error) {
      console.error("Error updating Vacancy status:", error);
      showToast({
        message: error.message || "Failed to update Vacancy status",
        type: "error",
      });
    }
  };

  const containerClasses = `w-[90%] m-auto mt-10 ${location.pathname === "/vacancies" ? "p-6" : ""}`;

  const handleCompanyChange = (selected) => {
    setSelectedCampaign(null);
    setSelectedCompany(selected?.value || null);
    if (serverPagination.current_page != 1)
      setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleCampaignChange = (selected) => {
    setSelectedCampaign(selected?.value || null);
    if (serverPagination.current_page != 1)
      setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleApplicantsClick = (vacancy) => {
    // Try multiple ways to get company ID (check root level first, then nested)
    const companyId =
      vacancy.company_id ||
      vacancy.campaign?.company?.id ||
      vacancy.campaign?.company_id;

    if (!companyId) {
      console.error("Company ID not found in vacancy:", vacancy);
      showToast({
        message:
          "Company information not available for this vacancy. Please ensure the vacancy has a valid campaign and company association.",
        type: "error",
      });
      return;
    }

    setActiveTab("applications");
    navigate(`/companies/view/${companyId}`, {
      state: {
        selectedVacancy: vacancy.id,
        fromApplicants: true,
      },
    });
  };

  // Create dropdown options
  const companyOptions = [
    ...companies
      .map((company) => ({
        label: company.name,
        value: company.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const campaignOptions = [
    ...campaigns
      .map((campaign) => ({
        label: campaign.title,
        value: campaign.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const handlePageChange = (newPage) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: newPage,
    }));
  };

  // Handle pagination dropdown change
  const handlePaginationChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage("all-vacancies-table", newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1, // Reset to first page when changing items per page
    }));
  };

  const [isRefetching, setIsRefetching] = useState(false);
  useEffect(() => {
    const refetch = async () => {
      setIsRefetching(true);
      await refetchAllVacancies();
      setIsRefetching(false);
    };
    refetch();
  }, [
    serverPagination.current_page,
    serverPagination.per_page,
    searchTerm,
    selectedCompany,
    selectedCampaign,
  ]);

  // handle all vacancies data
  useEffect(() => {
    const processVacancies = async () => {
      // console.log("allVacanciesData", allVacanciesData);
      setFilteredVacancies([]);
      if (allVacanciesData.length > 0) {
        const mappedVacancies = mapVacancyData({
          vacancies: [...allVacanciesData],
        });
        const filtered = mappedVacancies; //.filter(item => item.status != 2);
        setFilteredVacancies(filtered);
      }
      if (pagination) {
        setServerPagination(pagination);
      }
    };
    processVacancies();
  }, [allVacanciesData, pagination]);

  // useEffect(() => {
  //   console.log("serverPagination", serverPagination);
  // }, [serverPagination]);

  return (
    <div className="w-[90%] mx-auto pt-12 pb-24 flex flex-col justify-start items-start gap-8 px-8">
      {/* Page Header */}
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
          <div className="flex-1 min-w-80 inline-flex flex-col justify-start items-start gap-1">
            <h1 className="self-stretch justify-start text-foreground text-2xl font-semibold font-['Inter'] leading-8">
              Vacancies
            </h1>
            <p className="self-stretch justify-start text-muted-foreground text-base font-normal font-['Inter'] leading-6">
              Manage and track all open job positions across your recruitment
              campaigns.
            </p>
          </div>
        </div>
      </div>

      {/* Controls Section: Search and Dropdowns */}
      <div className="self-stretch flex flex-col justify-start items-start gap-6">
        <div className="self-stretch inline-flex justify-between items-start gap-4 flex-wrap">
          {/* Search Box */}
          <div className="w-[384px] flex flex-col justify-start items-start gap-1.5 font-['Inter']">
            <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
              <div className="inline-flex justify-start items-center gap-1">
                <span className="text-muted-foreground text-sm font-medium leading-5">
                  Search
                </span>
                <span className="text-foreground text-sm font-medium leading-5">
                  *
                </span>
              </div>
              <div className="h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
                <Search className="w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search"
                  value={tempSearchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col gap-1.5">
              <label className="text-muted-foreground text-sm font-medium font-['Inter']">
                Company
              </label>
              <div className="relative z-10">
                <Dropdown
                  options={companyOptions}
                  value={
                    selectedCompany
                      ? {
                          label: companies.find((c) => c.id === selectedCompany)
                            ?.name,
                          value: selectedCompany,
                        }
                      : { label: "All Companies", value: "" }
                  }
                  onChange={handleCompanyChange}
                  placeholder="Select Company"
                  searchable={true}
                  className="w-[160px]"
                  dropdownWidth="w-[300px]"
                  allowAll={true}
                  allLabel="All Companies"
                  triggerClassName="!bg-primary-dark !h-8 !px-2.5 !py-1.5 !rounded-lg !border-none shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 !text-white"
                  customArrow={<ChevronDown className="w-4 h-4 text-white" />}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-muted-foreground text-sm font-medium font-['Inter']">
                Campaign
              </label>
              <div className="relative z-10">
                <Dropdown
                  options={campaignOptions}
                  value={
                    selectedCampaign
                      ? {
                          label: campaigns.find(
                            (c) => c.id === selectedCampaign,
                          )?.title,
                          value: selectedCampaign,
                        }
                      : { label: "All Campaigns", value: "" }
                  }
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  searchable={true}
                  className="w-[160px]"
                  dropdownWidth="w-[300px]"
                  allowAll={true}
                  allLabel="All Campaigns"
                  triggerClassName="!bg-primary-dark !h-8 !px-2.5 !py-1.5 !rounded-lg !border-none shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 !text-white"
                  customArrow={<ChevronDown className="w-4 h-4 text-white" />}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isErrorAllVacancies ? (
        <div className="w-full p-4 text-red-400 bg-red-100 rounded-lg text-center">
          Error loading vacancies
        </div>
      ) : isLoadingAllVacancies || isRefetching ? (
        <div className="flex w-full min-h-[400px] items-center justify-center bg-card rounded-xl shadow-sm border border-border">
          <Loader bgColor="transparent" />
        </div>
      ) : (
        <div className="self-stretch flex flex-col gap-6">
          <VacancyTable
            vacancies={filteredVacancies}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleStatusChange={handleStatusChange}
            onApplicantsClick={handleApplicantsClick}
            companyId={selectedCompany}
            handleSearchChange={handleSearchChange}
            searchTerm={tempSearchTerm}
            serverPagination={serverPagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePaginationChange}
          />
        </div>
      )}
    </div>
  );
};

export default AllVacancies;
